/**
 * PATH: packages/email-hub/src/feature/components/email-preview-pane/PreviewReadingSurface.tsx
 * FILE_ID: EMAILHUB-EMAILPREVIEWPANE-READING-SURFACE-001
 * PURPOSE: Render sender metadata, triage signals, body states, and thread.
 * NOTES FOR FUTURE AI: The legacy identity/strip/ping block is the flag-OFF
 *   safety net for change #269 — with `wholeReaderHeader` ON the shared
 *   ReaderHeaderFrame owns those bands and this surface is body-only. Delete
 *   the legacy block only when the flag retires (plan Phase 9).
 */
import { AlertCircle, RefreshCw } from 'lucide-react';
import { EMAIL_ADDRESS_ATTR, InPageFindBar, RecipientList, getAvatarColor, getInitials } from '@quotech/ui';
import { cn } from '../../../_internal/lib/utils';
import { isEmailHubFeatureEnabled } from '../../../_internal/flags';
import { formatFullDate } from '../../lib/utils';
import { ReaderSignalStrip } from '../../signal-strip';
import { SmartActionBar } from '../SmartActionBar';
import { WorkflowRoleChip } from '../WorkflowRoleChip';
import { ReceivingMailboxVia } from '../RowAttributionChips';
import { PingNotificationCard } from '../PingNotificationCard';
import { usePingHandoff } from '../usePingHandoff';
import CalendarInviteCard from '../CalendarInviteCard';
import EmailThread from '../EmailThread';
import type { PreviewReadingSurfaceProps } from './types';

/** Verbatim legacy header content (pre-#269) — only rendered flag-OFF. */
function LegacyIdentityBlock({
  email,
  onRefetch,
  onPatchEmail,
  onResolved,
  onFilterCompany,
  allocationMembers,
  kanbanApi,
  kanbanOrgId,
  canRequestRule,
  openAllocatedCard,
  openAllocatedBoard,
  openBillDoc,
  setPeekJob,
  setRuleDialogOpen,
}: Pick<
  PreviewReadingSurfaceProps,
  | 'email'
  | 'onRefetch'
  | 'onPatchEmail'
  | 'onResolved'
  | 'onFilterCompany'
  | 'allocationMembers'
  | 'kanbanApi'
  | 'kanbanOrgId'
  | 'canRequestRule'
  | 'openAllocatedCard'
  | 'openAllocatedBoard'
  | 'openBillDoc'
  | 'setPeekJob'
  | 'setRuleDialogOpen'
>) {
  // This whole block only mounts flag-OFF (see parent), so the hook call
  // here is unconditional relative to ITS OWN mount, same rule as any other
  // component (change #269: PingNotificationCard no longer self-fetches).
  const pingHandoff = usePingHandoff(email.id);
  return (
    <div className="px-4 pt-4 pb-3">
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shrink-0',
            getAvatarColor(email.from_name || email.from_address || '')
          )}
        >
          {getInitials(email.from_name || (email.from_address ?? '').split('@')[0] || '?')}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex min-w-0 items-center gap-2 flex-wrap">
            <span
              className="font-semibold text-sm text-foreground"
              {...(email.from_address ? { [EMAIL_ADDRESS_ATTR]: email.from_address } : {})}
            >
              {email.from_name || (email.from_address ?? '').split('@')[0] || 'Unknown sender'}
            </span>
            <span
              className="min-w-0 truncate text-xs text-muted-foreground select-text"
              {...(email.from_address ? { [EMAIL_ADDRESS_ATTR]: email.from_address } : {})}
            >
              &lt;{email.from_address || ''}&gt;
            </span>
            <WorkflowRoleChip email={email} />
          </div>
          <div className="mt-1 flex min-w-0 flex-wrap justify-end gap-1">
            <SmartActionBar
              email={email}
              onRefetch={onRefetch}
              onResolved={onResolved}
              onOpenCard={openAllocatedCard}
              onOpenBill={openBillDoc}
            />
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
            <RecipientList label="To" recipients={email.to_addresses} emptyText="me" max={3} />{' '}
            · {formatFullDate(email.received_at)}
            <ReceivingMailboxVia email={email} />
          </div>
          {email.cc_addresses && email.cc_addresses.length > 0 && (
            <div className="text-xs text-muted-foreground mt-0.5">
              <RecipientList label="Cc" recipients={email.cc_addresses} max={3} />
            </div>
          )}
          <ReaderSignalStrip
            email={email}
            api={kanbanApi}
            orgId={kanbanOrgId}
            onRefetch={onRefetch}
            onPatchEmail={onPatchEmail}
            onFilterCompany={onFilterCompany}
            onOpenCard={openAllocatedCard}
            onOpenBoard={openAllocatedBoard}
            onPeekJob={setPeekJob}
            canRequestRule={canRequestRule}
            onOpenRuleDialog={() => setRuleDialogOpen(true)}
            allocationMembers={allocationMembers}
          />
        </div>
      </div>
      <div className="mt-3">
        <PingNotificationCard
          emailId={email.id}
          allocationSummary={email.allocation_summary}
          handoff={pingHandoff}
        />
      </div>
    </div>
  );
}

export function PreviewReadingSurface(props: PreviewReadingSurfaceProps) {
  const {
    email,
    onContextMenu,
    onRefetch,
    bodyLoading,
    detailError,
    onRetryDetail,
    combinedBodyRef,
    find,
  } = props;
  const wholeHeaderEnabled = isEmailHubFeatureEnabled('wholeReaderHeader');

  return (
    <div className="flex-1 overflow-hidden flex min-h-0" onContextMenu={onContextMenu}>
      <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
        {!wholeHeaderEnabled && <LegacyIdentityBlock {...props} />}
        {email.calendar_event && (
          <div className="px-4 pt-3 pb-4">
            <CalendarInviteCard event={email.calendar_event} />
          </div>
        )}
        {email.has_attachments && (email.attachments?.length ?? 0) === 0 && (
          <div className="px-4 pb-4">
            <div className="bg-muted/30 rounded-lg p-3 text-xs text-muted-foreground flex items-center gap-2">
              <RefreshCw className="h-3 w-3 animate-spin" />
              Loading attachments…
            </div>
          </div>
        )}
        {detailError && !email.body_html && (
          <div className="mx-4 mb-3 p-2.5 bg-destructive/10 border border-destructive/20 rounded-md flex items-center gap-2">
            <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
            <span className="text-xs text-destructive flex-1 min-w-0">
              Couldn't load the full message - showing a preview only.
            </span>
            {onRetryDetail && (
              <button
                type="button"
                onClick={onRetryDetail}
                className="text-xs font-medium text-destructive underline-offset-2 hover:underline shrink-0"
              >
                Retry
              </button>
            )}
          </div>
        )}
        <div className="px-4 pb-4 min-w-0" ref={combinedBodyRef}>
          <EmailThread
            key={email.id}
            openedEmail={email}
            onRefetch={() => onRefetch?.()}
            openedBodyLoading={bodyLoading}
            findQuery={find.isOpen ? find.bar.query : ''}
            collapseOlder={props.collapseOlderMessages}
          />
        </div>
        <InPageFindBar {...find.bar} />
      </div>
    </div>
  );
}
